import React from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';

/**
 * Catches render errors anywhere below it. Without this, a single thrown error
 * unmounts the whole React tree and the user is left looking at a blank page
 * with no indication of what happened.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Kept as console.error so the stack still reaches the browser console and
    // any error-reporting tool wired up later.
    console.error('Unhandled UI error:', error, info?.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-zip-bg">
        <div className="zip-card p-8 max-w-lg w-full space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-zip-red/10 flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={20} className="text-zip-red" />
            </div>
            <div>
              <h1 className="font-semibold text-zip-text tracking-tight">Something went wrong</h1>
              <p className="text-xs text-zip-muted mt-0.5">
                This screen hit an unexpected error. Your data has not been changed.
              </p>
            </div>
          </div>

          <p className="text-xs font-mono text-zip-muted bg-zip-hover border border-zip-border rounded-lg px-3 py-2 break-words">
            {this.state.error?.message || 'Unknown error'}
          </p>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="btn-primary flex items-center gap-2 text-sm"
            >
              <RotateCcw size={14} /> Reload the page
            </button>
            <button
              type="button"
              onClick={() => { window.location.href = '/'; }}
              className="btn-ghost text-sm"
            >
              Back to start
            </button>
          </div>

          <p className="text-[11px] text-zip-muted/70">
            If this keeps happening, note what you were doing and report it — the full
            details are in the browser console.
          </p>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
